import { bool, object, string } from 'yup'
import { translate } from '$i18n/i18n'
import { hashString } from '$utils/utils.svelte'

// Pattern for validation of a street number.
export const STREET_NUMBER_PATTERN =
	/(\d+|\d+\s*[a-zA-Z]|\d+\s*[a-zA-Z]?\s*[/-]\s*\d+\s*[a-zA-Z]?)$/
// Pattern to separate street number from street name (at least one whitespace character in front).
export const STREET_NUMBER_PATTERN_WITH_SPACE =
	/\s+(\d+|\d+\s*[a-zA-Z]|\d+\s*[a-zA-Z]?\s*[/-]\s*\d+\s*[a-zA-Z]?)$/

export const COORDINATE_PATTERN = /^-?\d{1,2}(\.\d+)?,\s*-?\d{1,3}(\.\d+)?$/

export type AddressData = {
	city: string
	zip: string
	street: string
	houseNumber?: string
	id?: string // hash of the autocompletion suggestion
	text?: string
	anonymize?: boolean
}

export function initAddressData(): AddressData {
	const defaults = {
		city: '',
		zip: '',
		street: '',
		houseNumber: '',
		id: '',
		text: '',
		anonymize: true
	}

	return {
		...defaults
	}
}

export function DefaultAddressData() {
	return {
		city: '',
		street: '',
		zip: '',
		houseNumber: '',
		id: '',
		text: '',
		anonymize: true
	}
}

export function copyAddressData(address: AddressData) {
	const copy: AddressData = DefaultAddressData()
	Object.assign(copy, address)
	return copy
}

export function addressDataToString(addressData: AddressData) {
	if (!addressData) return ''
	const streetAndHouseNumber = `${addressData.street}${addressData.street && addressData.houseNumber ? ' ' + addressData.houseNumber : ''}`
	const zipAndCity = [addressData.zip, addressData.city].filter(Boolean).join(' ')
	if (!streetAndHouseNumber && !zipAndCity) {
		return ''
	}
	if (!streetAndHouseNumber || addressData.anonymize) {
		return zipAndCity
	}
	return `${streetAndHouseNumber}, ${zipAndCity}`
}

export function generateAddressDataId(address: AddressData) {
	const str = addressDataToString(address)
	return hashString(str)
}

export function getAddressDataAutocompleteText(addressData: AddressData) {
	return `${addressData.street} ${addressData.zip} ${addressData.city}`
}

export const addressSchema = object({
	// id: string().notRequired(), not needed by server
	// text: string().notRequired().trim(), not needed by server
	houseNumber: string()
		.trim()
		.optional()
		.nullable()
		.test('matches-pattern', 'Invalid house number', (value) => {
			if (!value || value === '') return true // Allow empty
			// Be aware, if the tenant gets a street number field before the Sirius rework, this will be validated here as well.
			return STREET_NUMBER_PATTERN.test(value)
		}),
	city: string().required(translate('de', 'UI.validation.address.city.required', [])),
	zip: string()
		.trim()
		.required(translate('de', 'UI.validation.address.zip.required', []))
		.min(5, translate('de', 'UI.validation.address.zip.tooShort', []))
		.max(5, translate('de', 'UI.validation.address.zip.tooLong', []))
		.matches(/^\d{5}$/, translate('de', 'UI.validation.address.zip.onlyNumbers', [])),
	street: string().required(translate('de', 'UI.validation.address.street.required', [])),
	anonymize: bool().optional()
})
